//================================================================
//  Component: Admin User Table
//================================================================

//  Purpose: Generic table for modifying and granting user permissions.

//================================================================

// Libraries
import React, { useContext, useState, useEffect } from 'react';

// Contexts
import { GetRoles, GetUser } from '../../../../../../Library/GlobalContexts';

// Components
import AdminUserTableDeleteUser from './AdminUserTableDeleteUser';
import AdminUserTableAddUser from './AdminUserTableAddUser';
import AdminUserTableCheckBox from './AdminUserTableCheckBox';
import AdminUserGroupsPane from './AdminUserGroupsPane';

//Images
import IconSearch from '../../../../../../Components/Images/Icon_Search_Grey.svg';
import DeleteDisabledIcon from '../../../../../../Components/Images/Icon_DeleteDisabled_Grey.svg';
import iconDelete from '../../../../../../Components/Images/Icon_DeleteDefault_Red.svg';


export default function AdminUserTable({
    documentid,
    permissionsData,
}) {

    //------------------------------------------------------
    //  useContext
    //------------------------------------------------------

    const getUser = useContext(GetUser);
    const getRoles = useContext(GetRoles);

    //------------------------------------------------------
    //  useState
    //------------------------------------------------------

    // Search Inputs
    const [searchInputAdmins, setSearchInputAdmins] = useState('');
    const [searchInputUsers, setSearchInputUsers] = useState('');

    // Holds all the admins within the table
    const [admins, setAdmins] = useState([]);
    const [adminsFiltered, setAdminsFiltered] = useState([]);

    // Holds all the users within the table
    const [users, setUsers] = useState([]);
    const [usersFiltered, setUsersFiltered] = useState([]);

    // Open the 'Add Users' Modal
    //  type -> 'user' or 'admin'
    //  users -> array of 'admins' AND 'users'
    const [openAddModal, setOpenAddModal] = useState({ 'type': '', 'users': [] });

    // Open the 'Delete Admin' or 'Delete User' Modal -> 'undefined' or 'user object'
    const [openDeleteModal, setOpenDeleteModal] = useState(undefined);

    // Open close the groups pane
    const [openGroupsPane, setOpenGroupsPane] = useState(false);

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Handles search function
    function handleSearch(value, tableid) {

        if (tableid === undefined) throw Error('handleSearch ~ Unable to complete WITHOUT the tableid param.')

        // Update useState
        if (tableid === 'Admins') {

            setSearchInputAdmins(value);

        } else {

            setSearchInputUsers(value);

        }

        // Reset the Filter (Admins)
        if (value.length === 0 && tableid === 'Admins') {

            setAdminsFiltered(admins);

            return;

        };

        // Reset the Filter (Users)
        if (value.length === 0 && tableid === 'Users') {

            setUsersFiltered(users);

            return;

        };

        // Search Filter
        //  1. emailaddress
        //  2. givenname
        //  3. surname
        //  4. jobtitle
        const searchResults = (tableid === 'Admins' ? admins : users).filter((object) =>
            object?.emailaddress?.toLowerCase().includes(value.toLowerCase()) ||
            object?.givenname?.toString().toLowerCase().includes(value.toLowerCase()) ||
            object?.surname?.toString().toLowerCase().includes(value.toLowerCase()) ||
            object?.jobtitle?.toString().toLowerCase().includes(value.toLowerCase())
        );

        // Save the filtered search results to the useState
        tableid === 'Admins' ? setAdminsFiltered(searchResults) : setUsersFiltered(searchResults)

    };

    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------

    // onLoad
    //  1. Split admins and users
    useEffect(() => {

        if (documentid === undefined) return;
        if (permissionsData === undefined) return;
        if (getRoles === undefined) return;

        const tempAdmins = [];
        const tempUsers = [];

        permissionsData[documentid].forEach((emailaddress) => {

            if (emailaddress === undefined) return;
            if (getRoles[documentid][emailaddress]?.admin === undefined) return;

            // First, we need to find all 'admins'
            if (getRoles[documentid][emailaddress].admin === true) {

                // Array of admins objects
                tempAdmins.push({
                    'emailaddress': emailaddress,
                    ...getRoles[documentid][emailaddress] // Flatten the user 'roles' object
                });

                return;

            }

            // Now, let's filter OUT all the groups
            if (emailaddress === 'allUsers') return; // skip 'allUsers' group

            // Finally, find all the remaining 'users'
            tempUsers.push({
                'emailaddress': emailaddress,
                ...getRoles[documentid][emailaddress] // Flatten the user 'roles' object
            });

        });

        setAdmins(tempAdmins);
        setAdminsFiltered(tempAdmins);
        setUsers(tempUsers);
        setUsersFiltered(tempUsers);

    }, [documentid, permissionsData, getRoles])

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (

        <div>

            {/* --------------------------- */}
            {/*   Administrators | Section  */}
            {/* --------------------------- */}

            <div className='flex flex-col shadow-md p-4 rounded-lg'>

                {/* ===== Administrators | Table Header ===== */}
                <label className='mb-3 text-lg font-semibold'>Administrators</label>
                <div className='flex flex-row justify-between items-center bg-neutral-100 p-2 px-3 rounded-t-lg'>

                    {/* Label */}
                    <label className='font-semibold'>Total: {adminsFiltered.length} of {admins.length}</label>

                    <div className='flex flex-row gap-2'>

                        {/* Search Bar */}
                        <div className='grid grid-cols-[30px_1fr] items-center gap-[15px] bg-white px-[5px] rounded-[5px] border-1 border-solid border-[#D8D8D8] w-[300px] my-[4px]'>
                            <img className='ml-[10px]' src={IconSearch} alt='searchInputAdmins-icon'></img>
                            <label htmlFor='searchInputAdmins'>
                                <input
                                    id='searchInputAdmins'
                                    className='border-none h-[35px] p-0 m-0 outline-none'
                                    type='text'
                                    placeholder='Search'
                                    onChange={(e) => handleSearch(e.target.value, 'Admins')}
                                    value={searchInputAdmins}
                                    autoComplete='off'
                                ></input>
                            </label>
                        </div>

                        {/* + Add Admin */}
                        <button className='Primary-Button' onClick={() => setOpenAddModal({ 'type': 'admin', 'users': [...admins, ...users] })}>+ Add Admin</button>

                    </div>

                </div>

                {/* ===== Admins | Table Body ===== */}
                <table className='border border-solid border-[#D8D8D8]'>
                    <colgroup>
                        <col className='w-[500px]' colSpan={1}></col>
                        <col className='w-[100px]' colSpan={1}></col>
                        <col className='w-[100px]' colSpan={1}></col>
                        <col className='w-[100px]' colSpan={1}></col>
                        <col className='w-[100px]' colSpan={1}></col>
                        <col className='w-[100px]' colSpan={1}></col>
                        <col className='w-[50px]' colSpan={1}></col>
                    </colgroup>
                    <thead>
                        <tr className='border-b-[#D8D8D8] border-solid border-b overflow-visible'>

                            <th className='py-[15px] px-[20px] ml-[20px] font-medium'>
                                {/* Blank */}
                            </th>

                            <th className='py-[15px] px-[10px] font-medium'>
                                Read
                            </th>
                            <th className='py-[15px] px-[10px] font-medium'>
                                Download
                            </th>
                            <th className='py-[15px] px-[10px] font-medium'>
                                Create
                            </th>
                            <th className='py-[15px] px-[10px] font-medium'>
                                Modify
                            </th>
                            <th className='py-[15px] px-[10px] font-medium'>
                                Delete
                            </th>
                            <th className='py-[15px] px-[20px] font-medium'>
                                {/* Delete Icon */}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            // No admins found
                            adminsFiltered.length === 0 ?
                                (
                                    <tr>
                                        <td colSpan={7}>
                                            <div className='text-center p-[2%]'>
                                                No admins found.
                                            </div>
                                        </td>
                                    </tr>
                                )
                                :
                                (
                                    // Map in each admin
                                    adminsFiltered.map((userObject, index) => (

                                        <tr key={index} className='border-b-[#C8C8C9] border-solid border-b last:border-0'>

                                            <td className='flex flex-col gap-1 p-3 overflow-hidden'>
                                                <label className='text-md font-semibold'>{userObject?.givenname} {userObject?.surname}</label>
                                                <label className='text-[#424242] font-light'>{userObject?.emailaddress} - {userObject?.jobtitle}</label>
                                            </td>
                                            <td>
                                                <label style={{ marginLeft: '8px', marginBottom: '25px' }} className='Checkbox-Container'>
                                                    <input
                                                        type='checkbox'
                                                        checked={true}
                                                        disabled
                                                    ></input>
                                                    <span className='Checkbox cursor-not-allowed'></span>
                                                </label>
                                            </td>
                                            <td>
                                                <label style={{ marginLeft: '8px', marginBottom: '25px' }} className='Checkbox-Container'>
                                                    <input
                                                        type='checkbox'
                                                        checked={true}
                                                        disabled
                                                    ></input>
                                                    <span className='Checkbox cursor-not-allowed'></span>
                                                </label>
                                            </td>
                                            <td>
                                                <label style={{ marginLeft: '8px', marginBottom: '25px' }} className='Checkbox-Container'>
                                                    <input
                                                        type='checkbox'
                                                        checked={true}
                                                        disabled
                                                    ></input>
                                                    <span className='Checkbox cursor-not-allowed'></span>
                                                </label>
                                            </td>
                                            <td>
                                                <label style={{ marginLeft: '8px', marginBottom: '25px' }} className='Checkbox-Container'>
                                                    <input
                                                        type='checkbox'
                                                        checked={true}
                                                        disabled
                                                    ></input>
                                                    <span className='Checkbox cursor-not-allowed'></span>
                                                </label>
                                            </td>
                                            <td>
                                                <label style={{ marginLeft: '8px', marginBottom: '25px' }} className='Checkbox-Container'>
                                                    <input
                                                        type='checkbox'
                                                        checked={true}
                                                        disabled
                                                    ></input>
                                                    <span className='Checkbox cursor-not-allowed'></span>
                                                </label>
                                            </td>
                                            <td className='min-w-[30px] round'>
                                                {
                                                    // Anti-lockout rule for admins
                                                    getUser.emailaddress === userObject?.emailaddress ?
                                                            <img className='cursor-not-allowed' src={DeleteDisabledIcon} alt='Delete-Icon-Disabled'></img>
                                                        :
                                                            <img className='cursor-pointer' src={iconDelete} alt='Delete-Icon' onClick={() => setOpenDeleteModal(userObject)}></img>
                                                }
                                            </td>

                                        </tr>

                                    ))

                                )
                        }
                    </tbody>

                </table>

            </div>

            {/* --------------------------- */}
            {/*     Azure AD Groups | Section        */}
            {/* --------------------------- */}

            <div className='flex flex-col shadow-md border border-solid p-4 rounded-lg mt-4'>

                {/* ===== Azure AD Groups | Table Header ===== */}
                <label className='mb-3 text-lg font-semibold'>Groups</label>
                <div className='flex flex-row justify-between items-center bg-neutral-100 p-2 px-3 rounded-t-lg h-[62px]'>

                    {/* Label */}
                    <label className='font-semibold'>Total: 1 of 1</label>

                </div>

                {/* ===== Table Body ===== */}
                <table className='border border-solid border-[#D8D8D8]'>
                    <colgroup>
                        <col className='w-[500px]' colSpan={1}></col>
                        <col className='w-[100px]' colSpan={1}></col>
                        <col className='w-[100px]' colSpan={1}></col>
                        <col className='w-[100px]' colSpan={1}></col>
                        <col className='w-[100px]' colSpan={1}></col>
                        <col className='w-[100px]' colSpan={1}></col>
                        <col className='w-[50px]' colSpan={1}></col>
                    </colgroup>
                    <thead>
                        <tr className='border-b-[#D8D8D8] border-solid border-b overflow-visible'>
                            <th className='py-[15px] px-[20px] ml-[20px] font-medium'>
                                {/* Blank */}
                            </th>

                            <th className='py-[15px] px-[10px] font-medium'>
                                Read
                            </th>
                            <th className='py-[15px] px-[10px] font-medium'>
                                Download
                            </th>
                            <th className='py-[15px] px-[10px] font-medium'>
                                Create
                            </th>
                            <th className='py-[15px] px-[10px] font-medium'>
                                Modify
                            </th>
                            <th className='py-[15px] px-[10px] font-medium'>
                                Delete
                            </th>
                            <th className='py-[15px] px-[20px] font-medium'>
                                {/* Delete Icon */}
                            </th>
                        </tr>
                    </thead>
                    <tbody>

                        {/* --------------------------- */}
                        {/*     Hardcoded Groups        */}
                        {/* --------------------------- */}

                        <tr className='border-b-[#C8C8C9] border-solid border-b last:border-0'>

                            <td className='flex flex-col gap-1 p-3 overflow-hidden'>
                                <label className='text-md font-semibold underline text-[#0972D2] cursor-pointer' onClick={() => setOpenGroupsPane(true)}>Lendlease Users</label>
                                <label className='text-[#424242] font-light'>Group of all Lendlease accounts within Active Directory</label>
                            </td>
                            <td>
                                <AdminUserTableCheckBox
                                    documentid={documentid}
                                    userObject={{
                                        ...getRoles[documentid]['allUsers'],
                                        'emailaddress': 'allUsers', // append in the emailaddress field
                                    }}
                                    roleId={'read'}
                                ></AdminUserTableCheckBox>
                            </td>
                            <td>
                                <AdminUserTableCheckBox
                                    documentid={documentid}
                                    userObject={{
                                        ...getRoles[documentid]['allUsers'],
                                        'emailaddress': 'allUsers', // append in the emailaddress field
                                    }}
                                    roleId={'download'}
                                ></AdminUserTableCheckBox>
                            </td>
                            <td>
                                <AdminUserTableCheckBox
                                    documentid={documentid}
                                    userObject={{
                                        ...getRoles[documentid]['allUsers'],
                                        'emailaddress': 'allUsers', // append in the emailaddress field
                                    }}
                                    roleId={'create'}
                                ></AdminUserTableCheckBox>
                            </td>
                            <td>
                                <AdminUserTableCheckBox
                                    documentid={documentid}
                                    userObject={{
                                        ...getRoles[documentid]['allUsers'],
                                        'emailaddress': 'allUsers', // append in the emailaddress field
                                    }}
                                    roleId={'write'}
                                ></AdminUserTableCheckBox>
                            </td>
                            <td>
                                <AdminUserTableCheckBox
                                    documentid={documentid}
                                    userObject={{
                                        ...getRoles[documentid]['allUsers'],
                                        'emailaddress': 'allUsers', // append in the emailaddress field
                                    }}
                                    roleId={'delete'}
                                ></AdminUserTableCheckBox>
                            </td>

                            <td className='min-w-[30px] round'>
                                <img className='cursor-not-allowed' src={DeleteDisabledIcon} alt='Delete-Icon-Disabled'></img>
                            </td>

                        </tr>

                    </tbody>

                </table>

            </div>

            {/* --------------------------- */}
            {/*     Users | Section         */}
            {/* --------------------------- */}

            <div className='flex flex-col shadow-md border border-solid p-4 rounded-lg mt-4'>

                {/* ===== Users | Table Header ===== */}
                <label className='mb-3 text-lg font-semibold'>Users</label>
                <div className='flex flex-row justify-between items-center bg-neutral-100 p-2 px-3 rounded-t-lg'>

                    {/* Label */}
                    <label className='font-semibold'>Total: {usersFiltered.length} of {users.length}</label>

                    <div className='flex flex-row gap-2'>

                        {/* Search Bar */}
                        <div className='grid grid-cols-[30px_1fr] items-center gap-[15px] bg-white px-[5px] rounded-[5px] border-1 border-solid border-[#D8D8D8] w-[300px] my-[4px]'>
                            <img className='ml-[10px]' src={IconSearch} alt='searchInputUsers-icon'></img>
                            <label htmlFor='searchInputUsers'>
                                <input
                                    id='searchInputUsers'
                                    className='border-none h-[35px] p-0 m-0 outline-none'
                                    type='text'
                                    placeholder='Search'
                                    onChange={(e) => handleSearch(e.target.value, 'Users')}
                                    value={searchInputUsers}
                                    autoComplete='off'
                                ></input>
                            </label>
                        </div>

                        {/* + Add User */}
                        <button className='Primary-Button' onClick={() => setOpenAddModal({ 'type': 'user', 'users': [...admins, ...users] })}>+ Add User</button>

                    </div>

                </div>

                {/* ===== Users | Table Body ===== */}
                <table className='border border-solid border-[#D8D8D8]'>
                    <colgroup>
                        <col className='w-[500px]' colSpan={1}></col>
                        <col className='w-[100px]' colSpan={1}></col>
                        <col className='w-[100px]' colSpan={1}></col>
                        <col className='w-[100px]' colSpan={1}></col>
                        <col className='w-[100px]' colSpan={1}></col>
                        <col className='w-[100px]' colSpan={1}></col>
                        <col className='w-[50px]' colSpan={1}></col>
                    </colgroup>
                    <thead>
                        <tr className='border-b-[#D8D8D8] border-solid border-b overflow-visible'>
                            <th className='py-[15px] px-[20px] ml-[20px] font-medium'>
                                {/* Blank */}
                            </th>

                            <th className='py-[15px] px-[10px] font-medium'>
                                Read
                            </th>
                            <th className='py-[15px] px-[10px] font-medium'>
                                Download
                            </th>
                            <th className='py-[15px] px-[10px] font-medium'>
                                Create
                            </th>
                            <th className='py-[15px] px-[10px] font-medium'>
                                Modify
                            </th>
                            <th className='py-[15px] px-[10px] font-medium'>
                                Delete
                            </th>
                            <th className='py-[15px] px-[20px] font-medium'>
                                {/* Delete Icon */}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            // No users found
                            usersFiltered.length === 0 ?
                                (
                                    <tr>
                                        <td colSpan={7}>
                                            <div className='text-center p-[2%]'>
                                                No users found.
                                            </div>
                                        </td>
                                    </tr>
                                )
                                :
                                (
                                    usersFiltered.map((userObject, index) => (

                                        <tr key={index} className='border-b-[#C8C8C9] border-solid border-b last:border-0'>

                                            <td className='flex flex-col gap-1 p-3 overflow-hidden'>
                                                <label className='text-md font-semibold'>{userObject?.givenname} {userObject?.surname}</label>
                                                <label className='text-[#424242] font-light'>{userObject?.emailaddress} - {userObject?.jobtitle}</label>
                                            </td>
                                            <td>
                                                <label style={{ marginLeft: '8px', marginBottom: '25px' }} className='Checkbox-Container'>
                                                    <input
                                                        type='checkbox'
                                                        checked={true}
                                                        disabled
                                                    ></input>
                                                    <span className='Checkbox cursor-not-allowed'></span>
                                                </label>
                                            </td>
                                            <td>
                                                <AdminUserTableCheckBox
                                                    documentid={documentid}
                                                    userObject={userObject}
                                                    roleId={'download'}
                                                ></AdminUserTableCheckBox>
                                            </td>
                                            <td>
                                                <AdminUserTableCheckBox
                                                    documentid={documentid}
                                                    userObject={userObject}
                                                    roleId={'create'}
                                                ></AdminUserTableCheckBox>
                                            </td>
                                            <td>
                                                <AdminUserTableCheckBox
                                                    documentid={documentid}
                                                    userObject={userObject}
                                                    roleId={'write'}
                                                ></AdminUserTableCheckBox>
                                            </td>
                                            <td>
                                                <AdminUserTableCheckBox
                                                    documentid={documentid}
                                                    userObject={userObject}
                                                    roleId={'delete'}
                                                ></AdminUserTableCheckBox>
                                            </td>
                                            <td className='min-w-[30px] round'>
                                                <img className='cursor-pointer' src={iconDelete} alt='Delete-Icon' onClick={() => setOpenDeleteModal(userObject)}></img>
                                            </td>

                                        </tr>

                                    ))
                                )
                        }
                    </tbody>

                </table>

            </div>

            {/* --------------------------- */}
            {/*     Modals & Panes          */}
            {/* --------------------------- */}

            <AdminUserTableDeleteUser
                documentid={documentid}
                openDeleteModal={openDeleteModal}
                setOpenDeleteModal={setOpenDeleteModal}
            ></AdminUserTableDeleteUser>

            <AdminUserTableAddUser
                documentid={documentid}
                openAddModal={openAddModal}
                setOpenAddModal={setOpenAddModal}
            ></AdminUserTableAddUser>

            <AdminUserGroupsPane
                title={'Lendlease Users'}
                defaultUsersQuery={['usertype', '==', 'lendleaseuser']}
                paneOpen={openGroupsPane}
                setPaneOpen={setOpenGroupsPane}
            ></AdminUserGroupsPane>

        </div>

    )

}
